import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Megaphone, Package, BarChart3, Settings as SettingsIcon, LogOut, Wrench, LayoutGrid, FileText } from "lucide-react";
import { adminApi, setToken, clearToken, getToken } from "@/lib/adminApi";
import { tools, draftTools, categories, TOTAL_TOOLS } from "@/lib/toolsData";
import { AdsManager } from "@/admin/AdsManager";
import { AffiliatesManager } from "@/admin/AffiliatesManager";

const inputCls = "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await adminApi.post("/auth/login", { email, password });
      setToken(r.data.token); onLogin(r.data.user); toast.success("Welcome back");
    } catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm space-y-4 rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
        <div className="text-center">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white"><LayoutDashboard className="h-5 w-5" /></div>
          <h1 className="mt-3 font-heading text-xl font-black">Kovi Control Center</h1>
          <p className="text-sm text-neutral-500">Sign in to manage your site</p>
        </div>
        <input data-testid="admin-email" className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="admin-password" className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="admin-login-btn" disabled={busy} className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60">{busy ? "Signing in…" : "Sign In"}</button>
      </form>
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="rounded-xl border border-neutral-200 bg-white p-4">
    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</p>
    <p className="mt-1 font-heading text-2xl font-black">{value}</p>
    {sub && <p className="text-xs text-neutral-400">{sub}</p>}
  </div>
);

const Overview = () => {
  const [an, setAn] = useState(null);
  useEffect(() => { adminApi.get("/admin/analytics").then((r) => setAn(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Published Tools" value={TOTAL_TOOLS} sub="live to users" />
        <Stat label="Draft / Hidden" value={draftTools.length} sub="not visible" />
        <Stat label="Categories" value={categories.length} />
        <Stat label="Ad Impressions" value={an?.totals?.impressions ?? "—"} sub={`${an?.totals?.clicks ?? 0} clicks`} />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Active Ads" value={an?.totals?.ads ?? "—"} />
        <Stat label="Overall CTR" value={an ? `${an.totals.ctr}%` : "—"} />
        <Stat label="Revenue (manual)" value={an ? `$${an.totals.revenue}` : "—"} />
        <Stat label="Affiliate Products" value={an?.top_affiliates?.length ?? "—"} />
      </div>
    </div>
  );
};

const Analytics = () => {
  const [an, setAn] = useState(null);
  const load = () => adminApi.get("/admin/analytics").then((r) => setAn(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setRev = async (id, v) => { await adminApi.patch(`/admin/ads/${id}/revenue`, { revenue: Number(v) }); load(); };
  if (!an) return <p className="text-sm text-neutral-400">Loading…</p>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Impressions" value={an.totals.impressions} />
        <Stat label="Clicks" value={an.totals.clicks} />
        <Stat label="CTR" value={`${an.totals.ctr}%`} />
        <Stat label="Revenue" value={`$${an.totals.revenue}`} />
      </div>
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="border-b border-neutral-200 px-4 py-3 font-heading font-bold">Top performing ads</div>
        <table className="w-full text-left text-sm">
          <thead className="bg-neutral-50 text-xs uppercase text-neutral-500"><tr><th className="px-4 py-2">Ad</th><th className="px-4 py-2">Imp</th><th className="px-4 py-2">Clk</th><th className="px-4 py-2">CTR</th><th className="px-4 py-2">Revenue ($)</th></tr></thead>
          <tbody>
            {an.top_ads.length === 0 ? <tr><td colSpan={5} className="px-4 py-6 text-center text-neutral-400">No data yet</td></tr> :
              an.top_ads.map((a) => (
                <tr key={a.id} className="border-b border-neutral-100 last:border-0">
                  <td className="px-4 py-2 font-medium">{a.name}</td>
                  <td className="px-4 py-2">{a.impressions}</td>
                  <td className="px-4 py-2">{a.clicks}</td>
                  <td className="px-4 py-2">{a.ctr}%</td>
                  <td className="px-4 py-2"><input type="number" defaultValue={a.revenue} onBlur={(e) => setRev(a.id, e.target.value)} className="w-24 rounded border border-neutral-300 px-2 py-1 text-xs" /></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Settings = ({ user }) => {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState("");
  const change = async () => {
    try { await adminApi.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password changed"); setCur(""); setNw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="max-w-md space-y-4">
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <p className="text-sm text-neutral-500">Signed in as</p>
        <p className="font-heading font-bold">{user.email}</p>
        <p className="text-xs text-neutral-400">Role: {user.role}</p>
      </div>
      <div className="space-y-3 rounded-xl border border-neutral-200 bg-white p-5">
        <h3 className="font-heading font-bold">Change password</h3>
        <input className={inputCls} type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
        <input className={inputCls} type="password" placeholder="New password (min 8 chars)" value={nw} onChange={(e) => setNw(e.target.value)} />
        <button data-testid="change-pw-btn" onClick={change} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">Update password</button>
      </div>
    </div>
  );
};

const NAV = [
  ["overview", "Dashboard", LayoutDashboard],
  ["ads", "Advertisements", Megaphone],
  ["affiliates", "Affiliates", Package],
  ["analytics", "Analytics", BarChart3],
  ["settings", "Settings", SettingsIcon],
];

const AdminApp = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    adminApi.get("/auth/me").then((r) => setUser(r.data)).catch(() => clearToken()).finally(() => setChecking(false));
  }, []);

  const logout = () => { clearToken(); setUser(null); };

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-neutral-100 text-neutral-400">Loading…</div>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="flex min-h-screen bg-neutral-100 text-neutral-900">
      <aside className="flex w-60 shrink-0 flex-col border-r border-neutral-200 bg-white">
        <div className="flex items-center gap-2 border-b border-neutral-200 px-5 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white"><LayoutDashboard className="h-4 w-4" /></div>
          <span className="font-heading font-black tracking-tight">Kovi Control</span>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map(([id, label, Icon]) => (
            <button key={id} data-testid={`admin-nav-${id}`} onClick={() => setTab(id)}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium ${tab === id ? "bg-blue-50 text-blue-700" : "text-neutral-600 hover:bg-neutral-100"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </nav>
        <div className="border-t border-neutral-200 p-3">
          <a href="/" className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100"><Wrench className="h-4 w-4" /> View site</a>
          <button data-testid="admin-logout" onClick={logout} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-6 lg:p-8">
        <h1 className="mb-6 font-heading text-2xl font-black capitalize">{NAV.find((n) => n[0] === tab)[1]}</h1>
        {tab === "overview" && <Overview />}
        {tab === "ads" && <AdsManager />}
        {tab === "affiliates" && <AffiliatesManager />}
        {tab === "analytics" && <Analytics />}
        {tab === "settings" && <Settings user={user} />}
      </main>
    </div>
  );
};

export default AdminApp;
